import React, { useEffect, useMemo, useState } from "react";
import {
  createJobOpening,
  deleteJobOpening,
  getJobOpenings,
  updateJobOpening,
} from "../api/recruitmentApi";
import { formatDate, normalizeList, statusClass } from "./hrUtils";
import "./HRShared.css";
import "./JobOpenings.css";

const COMPANY_OPTIONS = [
  "Sheth Design Consultants LLP",
  "Softvolute Buildcon LLP",
  "Magnira Universe LLP",
  "SDC Studio LLC",
];

const LOCATION_OPTIONS = ["Surat", "Ahmedabad", "Houston (USA)", "Gandhinagar"];

const STATUS_OPTIONS = [
  "Open",
  "Screening",
  "Interviewing",
  "Offer In Progress",
  "Closed",
  "On Hold",
];

const EMPLOYMENT_TYPES = ["Full Time", "Part Time", "Contract", "Internship"];

const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Urgent"];

const getEmptyForm = () => ({
  company: "",
  jobTitle: "",
  department: "",
  numberOfPositions: 1,
  priority: "Medium",
  employmentType: "Full Time",
  location: "",
  openingDate: new Date().toISOString().slice(0, 10),
  targetClosingDate: "",
  status: "Open",
  description: "",
});

const isClosed = (item) =>
  String(item?.status || "")
    .trim()
    .toLowerCase() === "closed";

const JobOpenings = () => {
  const [items, setItems] = useState([]);

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    company: "",
    location: "",
  });

  const [form, setForm] = useState(getEmptyForm());

  const [editingId, setEditingId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  /* =========================
     LOAD JOB OPENINGS
  ========================= */

  const loadItems = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await getJobOpenings({
        search: filters.search || undefined,
        status: filters.status || undefined,
        company: filters.company || undefined,
        location: filters.location || undefined,
        limit: 1000,
      });

      let list = normalizeList(response);

      // Frontend fallback filtering
      list = list.filter((item) => {
        const searchValue = String(filters.search || "")
          .trim()
          .toLowerCase();

        const matchesSearch =
          !searchValue ||
          String(item?.jobTitle || "")
            .toLowerCase()
            .includes(searchValue) ||
          String(item?.department || "")
            .toLowerCase()
            .includes(searchValue) ||
          String(item?.company || "")
            .toLowerCase()
            .includes(searchValue) ||
          String(item?.location || "")
            .toLowerCase()
            .includes(searchValue);

        const matchesStatus =
          !filters.status ||
          String(item?.status || "")
            .trim()
            .toLowerCase() === filters.status.toLowerCase();

        const matchesCompany =
          !filters.company ||
          String(item?.company || "").toLowerCase() ===
            filters.company.toLowerCase();

        const matchesLocation =
          !filters.location ||
          String(item?.location || "").toLowerCase() ===
            filters.location.toLowerCase();

        return (
          matchesSearch && matchesStatus && matchesCompany && matchesLocation
        );
      });

      setItems(list);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to load job openings.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(loadItems, 250);

    return () => clearTimeout(timer);

    // Existing API loading behavior retained
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, filters.company, filters.location]);

  /* =========================
     ACTIVE JOBS ONLY
     Closed jobs excluded from KPI
  ========================= */

  const activeItems = useMemo(() => {
    return items.filter((item) => !isClosed(item));
  }, [items]);

  /* =========================
     SUMMARY TOTALS
  ========================= */

  const totals = useMemo(() => {
    const required = activeItems.reduce(
      (sum, item) => sum + Number(item?.numberOfPositions || 0),
      0,
    );

    const hired = activeItems.reduce(
      (sum, item) => sum + Number(item?.hiredCount || 0),
      0,
    );

    const remaining = activeItems.reduce((sum, item) => {
      const requiredCount = Number(item?.numberOfPositions || 0);
      const hiredCount = Number(item?.hiredCount || 0);

      return sum + Math.max(0, requiredCount - hiredCount);
    }, 0);

    return {
      total: activeItems.length,
      required,
      hired,
      remaining,
    };
  }, [activeItems]);

  /* =========================
     COMPANY-WISE SUMMARY
     Closed jobs excluded
  ========================= */

  const companySummary = useMemo(() => {
    return COMPANY_OPTIONS.map((companyName) => {
      const companyItems = activeItems.filter(
        (item) =>
          String(item?.company || "")
            .trim()
            .toLowerCase() === companyName.toLowerCase(),
      );

      return {
        company: companyName,

        openings: companyItems.length,

        required: companyItems.reduce(
          (sum, item) => sum + Number(item?.numberOfPositions || 0),
          0,
        ),

        hired: companyItems.reduce(
          (sum, item) => sum + Number(item?.hiredCount || 0),
          0,
        ),
      };
    });
  }, [activeItems]);

  /* =========================
     FILTERS
  ========================= */

  const updateFilter = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateForm = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      company: "",
      location: "",
    });
  };

  /* =========================
     ADD / EDIT
  ========================= */

  const openCreate = () => {
    setEditingId("");
    setForm(getEmptyForm());
    setError("");
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item._id);

    setForm({
      ...getEmptyForm(),
      ...item,

      company: item?.company || "",
      location: item?.location || "",

      openingDate: String(item?.openingDate || "").slice(0, 10) || "",

      targetClosingDate:
        String(item?.targetClosingDate || "").slice(0, 10) || "",
    });

    setError("");
    setModalOpen(true);
  };

  /* =========================
     SAVE
  ========================= */

  const submitForm = async (event) => {
    event.preventDefault();

    if (!form.company) {
      setError("Please select a company.");
      return;
    }

    if (!form.location) {
      setError("Please select a location.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = {
        ...form,

        company: String(form.company).trim(),
        jobTitle: String(form.jobTitle).trim(),
        department: String(form.department).trim(),
        location: String(form.location).trim(),

        numberOfPositions: Number(form.numberOfPositions),

        description: String(form.description || "").trim(),

        targetClosingDate: form.targetClosingDate || null,
      };

      if (editingId) {
        await updateJobOpening(editingId, payload);
      } else {
        await createJobOpening(payload);
      }

      setModalOpen(false);
      setEditingId("");
      setForm(getEmptyForm());

      await loadItems();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to save job opening.",
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     DELETE
  ========================= */

  const removeItem = async (id) => {
    const confirmed = window.confirm("Delete this job opening?");

    if (!confirmed) return;

    try {
      setError("");

      await deleteJobOpening(id);
      await loadItems();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to delete job opening.",
      );
    }
  };

  /* =========================
     JSX
  ========================= */

  return (
    <div className="hr-page">
      <header className="hr-page-header">
        <div>
          <h1>Job Openings</h1>

          <p>
            Create and monitor company-wise job openings, required employees and
            hiring progress.
          </p>
        </div>

        <button
          type="button"
          className="hr-btn hr-btn-primary"
          onClick={openCreate}
        >
          + Add Job Opening
        </button>
      </header>

      {error && <div className="hr-error">{error}</div>}

      {/* SUMMARY CARDS */}

      <section className="hr-kpi-grid hr-opening-kpis">
        <article className="hr-card hr-kpi">
          <div className="hr-kpi-label">Visible Openings</div>
          <div className="hr-kpi-value">{totals.total}</div>
        </article>

        <article className="hr-card hr-kpi">
          <div className="hr-kpi-label">Required Employees</div>
          <div className="hr-kpi-value">{totals.required}</div>
        </article>

        <article className="hr-card hr-kpi">
          <div className="hr-kpi-label">Employees Hired</div>
          <div className="hr-kpi-value">{totals.hired}</div>
        </article>

        <article className="hr-card hr-kpi">
          <div className="hr-kpi-label">Remaining Positions</div>
          <div className="hr-kpi-value">{totals.remaining}</div>
        </article>
      </section>

      {/* COMPANY-WISE CARDS */}

      <section className="hr-company-opening-grid">
        {companySummary.map((company) => (
          <button
            type="button"
            key={company.company}
            className={`hr-card hr-company-opening-card ${
              filters.company === company.company ? "active" : ""
            }`}
            onClick={() =>
              updateFilter(
                "company",
                filters.company === company.company ? "" : company.company,
              )
            }
          >
            <strong>{company.company}</strong>

            <span>
              {company.openings} opening
              {company.openings === 1 ? "" : "s"}
            </span>

            <small>
              Required: {company.required} · Hired: {company.hired}
            </small>
          </button>
        ))}
      </section>

      {/* FILTERS */}

      <section className="hr-card hr-panel">
        <div className="hr-filter-row hr-opening-filter-row">
          <input
            className="hr-input hr-opening-search"
            placeholder="Search title, department, company or location..."
            value={filters.search}
            onChange={(event) => updateFilter("search", event.target.value)}
          />

          <select
            className="hr-select"
            value={filters.company}
            onChange={(event) => updateFilter("company", event.target.value)}
          >
            <option value="">All companies</option>

            {COMPANY_OPTIONS.map((company) => (
              <option key={company} value={company}>
                {company}
              </option>
            ))}
          </select>

          <select
            className="hr-select"
            value={filters.location}
            onChange={(event) => updateFilter("location", event.target.value)}
          >
            <option value="">All locations</option>

            {LOCATION_OPTIONS.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </select>

          <select
            className="hr-select hr-opening-status"
            value={filters.status}
            onChange={(event) => updateFilter("status", event.target.value)}
          >
            <option value="">All statuses</option>

            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="hr-btn hr-btn-secondary"
            onClick={clearFilters}
          >
            Clear Filters
          </button>
        </div>
      </section>

      {/* JOB OPENINGS TABLE */}

      {loading ? (
        <div className="hr-card hr-loading">Loading job openings...</div>
      ) : (
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Position</th>
                <th>Department</th>
                <th>Location</th>
                <th>Required</th>
                <th>Hired</th>
                <th>Remaining</th>
                <th>Open Date</th>
                <th>Target Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {items.length ? (
                items.map((item) => {
                  const required = Number(item?.numberOfPositions || 0);

                  const hired = Number(item?.hiredCount || 0);

                  return (
                    <tr key={item._id}>
                      <td>
                        <strong>{item?.company || "-"}</strong>
                      </td>

                      <td>
                        <strong>{item?.jobTitle || "-"}</strong>
                      </td>

                      <td>{item?.department || "-"}</td>
                      <td>{item?.location || "-"}</td>

                      <td>{required}</td>
                      <td>{hired}</td>

                      <td>{Math.max(0, required - hired)}</td>

                      <td>{formatDate(item?.openingDate)}</td>

                      <td>{formatDate(item?.targetClosingDate)}</td>

                      <td>
                        <span
                          className={`hr-status ${statusClass(item?.status)}`}
                        >
                          {item?.status || "Open"}
                        </span>
                      </td>

                      <td>
                        <div className="hr-inline-actions">
                          <button
                            type="button"
                            className="hr-btn hr-btn-secondary"
                            onClick={() => openEdit(item)}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="hr-btn hr-btn-danger"
                            onClick={() => removeItem(item._id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="11" className="hr-empty">
                    No job openings found for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ADD / EDIT MODAL */}

      {modalOpen && (
        <div
          className="hr-modal-backdrop"
          onMouseDown={() => !saving && setModalOpen(false)}
        >
          <form
            className="hr-modal"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={submitForm}
          >
            <div className="hr-modal-head">
              <h2>{editingId ? "Edit Job Opening" : "Add Job Opening"}</h2>

              <button
                type="button"
                className="hr-btn hr-btn-secondary"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                Close
              </button>
            </div>

            <div className="hr-modal-body">
              <div className="hr-form-grid">
                {/* COMPANY */}

                <div className="hr-field">
                  <label>Company *</label>

                  <select
                    required
                    className="hr-select"
                    value={form.company}
                    onChange={(event) =>
                      updateForm("company", event.target.value)
                    }
                  >
                    <option value="">Select company</option>

                    {COMPANY_OPTIONS.map((company) => (
                      <option key={company} value={company}>
                        {company}
                      </option>
                    ))}
                  </select>
                </div>

                {/* LOCATION */}

                <div className="hr-field">
                  <label>Location *</label>

                  <select
                    required
                    className="hr-select"
                    value={form.location}
                    onChange={(event) =>
                      updateForm("location", event.target.value)
                    }
                  >
                    <option value="">Select location</option>

                    {LOCATION_OPTIONS.map((location) => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </select>
                </div>

                {/* JOB TITLE */}

                <div className="hr-field">
                  <label>Job title *</label>

                  <input
                    required
                    className="hr-input"
                    value={form.jobTitle}
                    onChange={(event) =>
                      updateForm("jobTitle", event.target.value)
                    }
                  />
                </div>

                {/* DEPARTMENT */}

                <div className="hr-field">
                  <label>Department *</label>

                  <input
                    required
                    className="hr-input"
                    value={form.department}
                    onChange={(event) =>
                      updateForm("department", event.target.value)
                    }
                  />
                </div>

                {/* REQUIRED EMPLOYEES */}

                <div className="hr-field">
                  <label>Required employees *</label>

                  <input
                    required
                    min="1"
                    type="number"
                    className="hr-input"
                    value={form.numberOfPositions}
                    onChange={(event) =>
                      updateForm("numberOfPositions", event.target.value)
                    }
                  />
                </div>

                {/* PRIORITY */}

                <div className="hr-field">
                  <label>Priority</label>

                  <select
                    className="hr-select"
                    value={form.priority}
                    onChange={(event) =>
                      updateForm("priority", event.target.value)
                    }
                  >
                    {PRIORITY_OPTIONS.map((priority) => (
                      <option key={priority} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>
                </div>

                {/* EMPLOYMENT TYPE */}

                <div className="hr-field">
                  <label>Employment type</label>

                  <select
                    className="hr-select"
                    value={form.employmentType}
                    onChange={(event) =>
                      updateForm("employmentType", event.target.value)
                    }
                  >
                    {EMPLOYMENT_TYPES.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                {/* STATUS */}

                <div className="hr-field">
                  <label>Status</label>

                  <select
                    className="hr-select"
                    value={form.status}
                    onChange={(event) =>
                      updateForm("status", event.target.value)
                    }
                  >
                    {STATUS_OPTIONS.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                {/* OPENING DATE */}

                <div className="hr-field">
                  <label>Opening date</label>

                  <input
                    type="date"
                    className="hr-input"
                    value={form.openingDate}
                    onChange={(event) =>
                      updateForm("openingDate", event.target.value)
                    }
                  />
                </div>

                {/* TARGET DATE */}

                <div className="hr-field">
                  <label>Target closing date</label>

                  <input
                    type="date"
                    className="hr-input"
                    value={form.targetClosingDate}
                    onChange={(event) =>
                      updateForm("targetClosingDate", event.target.value)
                    }
                  />
                </div>

                {/* DESCRIPTION */}

                <div className="hr-field hr-field-full">
                  <label>Description</label>

                  <textarea
                    className="hr-textarea"
                    value={form.description}
                    onChange={(event) =>
                      updateForm("description", event.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            <div className="hr-modal-foot">
              <button
                type="button"
                className="hr-btn hr-btn-secondary"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="hr-btn hr-btn-primary"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Opening"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default JobOpenings;
